import { useState, useEffect } from 'react'
import { X } from 'lucide-react'

interface SegmentProperties {
  name: string
  segmentType: 'MAGISTRALA' | 'ODNOGA' | 'PRZYŁĄCZE'
  diameter_mm?: number
  material?: string
  suw_id?: number
  parent_segment_id?: number
  segmentCategory: 'water' | 'sewer'
}

interface SUWOption {
  id: number
  name: string
}

interface SegmentOption {
  id: number
  name: string
  segment_type: string
}

interface SegmentPropertiesModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (properties: SegmentProperties) => void
  coordinates: [number, number][]
  suwOptions: SUWOption[]
  segmentOptions: SegmentOption[]
  clientId: number
  onSegmentCategoryChange?: (category: 'water' | 'sewer') => void
}

export default function SegmentPropertiesModal({
  isOpen,
  onClose,
  onSave,
  coordinates,
  suwOptions,
  segmentOptions,
  clientId,
  onSegmentCategoryChange
}: SegmentPropertiesModalProps) {
  const [properties, setProperties] = useState<SegmentProperties>({
    name: '',
    segmentType: 'MAGISTRALA',
    diameter_mm: undefined,
    material: '',
    suw_id: undefined,
    parent_segment_id: undefined,
    segmentCategory: 'water'
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setProperties({
        name: '',
        segmentType: 'MAGISTRALA',
        diameter_mm: undefined,
        material: '',
        suw_id: undefined,
        parent_segment_id: undefined,
        segmentCategory: 'water'
      })
      setErrors({})
    }
  }, [isOpen])

  // Auto-select first SUW for MAGISTRALA
  useEffect(() => {
    if (properties.segmentType === 'MAGISTRALA' && suwOptions.length > 0 && !properties.suw_id) {
      setProperties(prev => ({ ...prev, suw_id: suwOptions[0].id }))
    }
  }, [properties.segmentType, suwOptions])

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!properties.name.trim()) {
      newErrors.name = 'Nazwa odcinka jest wymagana'
    }

    if (!properties.segmentType) {
      newErrors.segmentType = 'Typ odcinka jest wymagany'
    }

    if (properties.segmentType === 'MAGISTRALA' && !properties.suw_id) {
      newErrors.suw_id = 'Magistrala musi być przypisana do SUW'
    }

    if ((properties.segmentType === 'ODNOGA' || properties.segmentType === 'PRZYŁĄCZE') && !properties.parent_segment_id) {
      newErrors.parent_segment_id = `${properties.segmentType === 'ODNOGA' ? 'Odnoga' : 'Przyłącz'} musi mieć segment nadrzędny`
    }

    if (properties.diameter_mm && (properties.diameter_mm <= 0 || properties.diameter_mm > 2000)) {
      newErrors.diameter_mm = 'Średnica musi być między 1 a 2000 mm'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = () => {
    if (!validateForm()) {
      return
    }

    // Clean up properties based on segment type
    const cleanedProperties = { ...properties }

    if (properties.segmentType !== 'MAGISTRALA') {
      cleanedProperties.suw_id = undefined
    }

    if (properties.segmentType === 'MAGISTRALA') {
      cleanedProperties.parent_segment_id = undefined
    }

    onSave(cleanedProperties)
  }

  const handleSegmentTypeChange = (segmentType: 'MAGISTRALA' | 'ODNOGA' | 'PRZYŁĄCZE') => {
    setProperties(prev => ({
      ...prev,
      segmentType,
      suw_id: segmentType === 'MAGISTRALA' && suwOptions.length > 0 ? suwOptions[0].id : undefined,
      parent_segment_id: segmentType !== 'MAGISTRALA' ? undefined : prev.parent_segment_id
    }))

    // Clear related errors
    setErrors(prev => {
      const newErrors = { ...prev }
      delete newErrors.segmentType
      delete newErrors.suw_id
      delete newErrors.parent_segment_id
      return newErrors
    })
  }

  const getFilteredSegmentOptions = () => {
    if (properties.segmentType === 'ODNOGA') {
      return segmentOptions.filter(seg => ['MAGISTRALA', 'ODNOGA'].includes(seg.segment_type))
    } else if (properties.segmentType === 'PRZYŁĄCZE') {
      return segmentOptions.filter(seg => ['MAGISTRALA', 'ODNOGA'].includes(seg.segment_type))
    }
    return []
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-stretch justify-center z-[3000] sm:items-center sm:p-4">
      <div className="bg-white sm:rounded-xl shadow-xl w-full sm:max-w-md flex flex-col max-h-full sm:max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-gray-200 shrink-0">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
            Właściwości odcinka {properties.segmentCategory === 'water' ? 'wodociągu' : 'kanalizacji'}
          </h2>
          <button
            onClick={onClose}
            className="p-1 -mr-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto px-5 sm:px-6 py-4 space-y-4">
          {/* Coordinates info */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-sm text-gray-600">
              <strong>Liczba punktów:</strong> {coordinates.length}
            </p>
            <p className="text-sm text-gray-600">
              <strong>Współrzędne:</strong> {coordinates[0][1].toFixed(6)}, {coordinates[0][0].toFixed(6)}
              {coordinates.length > 1 && ` → ${coordinates[coordinates.length - 1][1].toFixed(6)}, ${coordinates[coordinates.length - 1][0].toFixed(6)}`}
            </p>
          </div>

          {/* Segment Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Typ infrastruktury *
            </label>
            <select
              value={properties.segmentCategory}
              onChange={(e) => {
                const newCategory = e.target.value as 'water' | 'sewer'
                setProperties(prev => ({
                  ...prev,
                  segmentCategory: newCategory,
                  suw_id: undefined, // Clear SUW selection when changing category
                  parent_segment_id: undefined // Clear parent segment selection when changing category
                }))
                // Notify parent component to refetch infrastructure and segment options
                if (onSegmentCategoryChange) {
                  onSegmentCategoryChange(newCategory)
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="water">Wodociąg</option>
              <option value="sewer">Kanalizacja</option>
            </select>
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nazwa odcinka *
            </label>
            <input
              type="text"
              value={properties.name}
              onChange={(e) => setProperties(prev => ({ ...prev, name: e.target.value }))}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.name ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="np. Magistrala Główna"
            />
            {errors.name && (
              <p className="text-red-600 text-sm mt-1">{errors.name}</p>
            )}
          </div>

          {/* Segment Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Typ odcinka *
            </label>
            <select
              value={properties.segmentType}
              onChange={(e) => handleSegmentTypeChange(e.target.value as 'MAGISTRALA' | 'ODNOGA' | 'PRZYŁĄCZE')}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.segmentType ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="MAGISTRALA">Magistrala</option>
              <option value="ODNOGA">Odnoga</option>
              <option value="PRZYŁĄCZE">Przyłącz</option>
            </select>
            {errors.segmentType && (
              <p className="text-red-600 text-sm mt-1">{errors.segmentType}</p>
            )}
          </div>

          {/* SUW/Treatment Plant Selection - only for MAGISTRALA */}
          {properties.segmentType === 'MAGISTRALA' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {properties.segmentCategory === 'water' ? 'Stacja uzdatniania wody (SUW)' : 'Oczyszczalnia ścieków'} *
              </label>
              <select
                value={properties.suw_id || ''}
                onChange={(e) => setProperties(prev => ({ ...prev, suw_id: parseInt(e.target.value) }))}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.suw_id ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">
                  Wybierz {properties.segmentCategory === 'water' ? 'SUW' : 'oczyszczalnię'}...
                </option>
                {suwOptions.map(suw => (
                  <option key={suw.id} value={suw.id}>
                    {suw.name}
                  </option>
                ))}
              </select>
              {errors.suw_id && (
                <p className="text-red-600 text-sm mt-1">{errors.suw_id}</p>
              )}
            </div>
          )}

          {/* Parent Segment Selection - for ODNOGA and PRZYŁĄCZE */}
          {(properties.segmentType === 'ODNOGA' || properties.segmentType === 'PRZYŁĄCZE') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Segment nadrzędny *
              </label>
              <select
                value={properties.parent_segment_id || ''}
                onChange={(e) => setProperties(prev => ({ ...prev, parent_segment_id: parseInt(e.target.value) }))}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.parent_segment_id ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">
                  Wybierz segment nadrzędny...
                  {properties.segmentType === 'ODNOGA' ? ' (MAGISTRALA lub ODNOGA)' : ' (MAGISTRALA lub ODNOGA)'}
                </option>
                {getFilteredSegmentOptions().map(segment => (
                  <option key={segment.id} value={segment.id}>
                    {segment.name} ({segment.segment_type})
                  </option>
                ))}
              </select>
              {errors.parent_segment_id && (
                <p className="text-red-600 text-sm mt-1">{errors.parent_segment_id}</p>
              )}
            </div>
          )}

          {/* Diameter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Średnica (mm)
            </label>
            <input
              type="number"
              value={properties.diameter_mm || ''}
              onChange={(e) => setProperties(prev => ({
                ...prev,
                diameter_mm: e.target.value ? parseInt(e.target.value) : undefined
              }))}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.diameter_mm ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="np. 100"
              min="1"
              max="2000"
            />
            {errors.diameter_mm && (
              <p className="text-red-600 text-sm mt-1">{errors.diameter_mm}</p>
            )}
          </div>

          {/* Material */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Materiał
            </label>
            <select
              value={properties.material || ''}
              onChange={(e) => setProperties(prev => ({ ...prev, material: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Wybierz materiał...</option>
              {properties.segmentCategory === 'water' ? (
                <>
                  <option value="ŻELBET">Żelbet</option>
                  <option value="PVC">PVC</option>
                  <option value="PE">PE</option>
                  <option value="ŻELAZO">Żelazo</option>
                  <option value="STAL">Stal</option>
                  <option value="AZBESTOCEMENT">Azbestocement</option>
                  <option value="INNY">Inny</option>
                </>
              ) : (
                <>
                  <option value="beton">Beton</option>
                  <option value="tworzywo_gładkie">Tworzywo gładkie</option>
                  <option value="tworzywo_karbowane">Tworzywo karbowane</option>
                  <option value="ŻELBET">Żelbet</option>
                  <option value="PVC">PVC</option>
                  <option value="PE">PE</option>
                  <option value="ŻELAZO">Żelazo</option>
                  <option value="STAL">Stal</option>
                  <option value="AZBESTOCEMENT">Azbestocement</option>
                  <option value="INNY">Inny</option>
                </>
              )}
            </select>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3 px-5 sm:px-6 py-4 border-t border-gray-200 shrink-0">
          <button
            onClick={onClose}
            className="min-h-[44px] px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-lg transition-colors"
          >
            Anuluj
          </button>
          <button
            onClick={handleSave}
            className="min-h-[44px] px-4 py-2 text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 rounded-lg transition-colors"
          >
            Zapisz odcinek
          </button>
        </div>
      </div>
    </div>
  )
}
